import { Badge, Box, HStack, Spacer, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { useDuplicateBookingQuery } from '../../../features/activities/travellers/api/useDuplicateBookingQuery';
import { useContext, useMemo } from 'react';
import {
  BaseAlert,
  BaseButton,
  BaseCard,
  BaseDialog,
  BaseTabContext,
  BaseToolbarButton,
  BaseIcon
} from '@invara-dev/react-base';
import { formatDate } from '../../../common/formatters';
import { BaseTabContextType } from '@invara-dev/react-base/dist/BaseComponents/Tabs/BaseTabs/types';
import { ActivityTabData } from '../../../features/activities/ActivityTabs.tsx';
import { FlsqCopyBadge } from '../../../components/Common/badges/FlsqCopyBadge';
import { CopyableText } from '../../../components/Common/CopyableText';
import {
  faExternalLinkAlt,
  faUser,
  faBuilding,
  faMapMarkerAlt,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';

type Props = {
  /** Array of traveller IDs to check for duplicate bookings */
  travellerIds: number[];
  /** Check-in date to compare against existing bookings */
  checkInDate: string | undefined;
  /** Check-out date to compare against existing bookings */
  checkOutDate: string | undefined;
  /** Current activity ID to exclude from duplicate checks */
  activityId: number | undefined;
};

/**
 * Component that displays warnings for duplicate bookings found for the specified travellers.
 * Shows a warning alert with details button that opens a dialog containing information about
 * conflicting bookings including exact matches and date collisions.
 *
 * Features:
 * - Displays different alert levels based on conflict type (exact match vs collision)
 * - Shows copyable booking details with icons for easy identification
 * - Provides FLSQ copy functionality and activity opening capabilities
 * - Responsive layout that groups related information logically
 *
 * @param props - Component props containing traveller and booking information
 * @returns Warning alert component or empty fragment if no duplicates found
 *
 */
export const TravellerDuplicateBookingWarning = (props: Props) => {
  const disclosure = useDisclosure();
  const tabs = useContext<BaseTabContextType<ActivityTabData>>(BaseTabContext);

  const duplicateBookings = useDuplicateBookingQuery(
    props.travellerIds,
    props.checkInDate,
    props.checkOutDate,
    props.activityId
  );

  const duplicateTypes = useMemo(() => {
    if (!duplicateBookings.data) {
      return undefined;
    }
    const anyExactMatch = duplicateBookings.data?.find((booking) => booking.IsExact);
    const anyCollisions = duplicateBookings.data?.find((booking) => booking.IsCollision);
    return {
      anyExactMatch,
      anyCollisions
    };
  }, [duplicateBookings.data]);

  const getText = () => {
    if (duplicateTypes?.anyExactMatch) {
      return 'This booking is an exact match with another booking. It is very likely a duplicate!';
    } else if (duplicateTypes?.anyCollisions) {
      return "This booking's check in date is within another booking.";
    }
    return 'There are bookings with similar dates.';
  };

  const getAlertLevel = () => {
    if (duplicateTypes?.anyExactMatch) {
      return 'error';
    } else if (duplicateTypes?.anyCollisions) {
      return 'warning';
    }
    return 'info';
  };

  if (!duplicateBookings.data || duplicateBookings.data.length === 0) {
    return <></>;
  }

  return (
    <Box w={'100%'}>
      <BaseAlert status={getAlertLevel()}>
        <HStack gap='16px' paddingLeft={'8px'}>
          <VStack align={'start'} gap='0px'>
            <Text>{getText()}</Text>
            <Text>Please review before booking.</Text>
          </VStack>
          <Spacer />
          <BaseButton size='sm' minW='0' w='auto' padding={'0px 16px 0px 16px'} onClick={disclosure.onOpen}>
            Details
          </BaseButton>
        </HStack>
      </BaseAlert>

      {/* Overlay edit booking history filters component */}
      <BaseDialog
        disclosure={disclosure}
        dragDialog={{
          initialH: 700,
          initialW: 500
        }}
        header={'Existing bookings'}
        footerRender={<BaseButton onClick={disclosure.onClose}>Ok, understood.</BaseButton>}>
        <Box userSelect={'text'}>
          {duplicateBookings.data.map((value, index) => {
            return (
              <BaseCard
                key={`${index}-${value.TravellerName}`}
                variant={'elevated'}
                margin={'auto'}
                marginBottom={'16px'}
                paddingTop={'16px'}
                border={'1px solid rgb(221, 222, 224)'}
                borderRadius={'8px'}>
                <VStack gap={'12px'} padding={0} alignItems={'flex-start'}>
                  {value.IsExact && (
                    <BaseAlert status={'error'}>This booking is an exact match with the current booking.</BaseAlert>
                  )}
                  {value.IsCollision && !value.IsExact && (
                    <BaseAlert status={'warning'}>This booking is active while the current one is starting.</BaseAlert>
                  )}

                  <VStack gap={'8px'} alignItems={'flex-start'} width={'100%'}>
                    <CopyableText
                      value={value.TravellerName}
                      label='Traveller'
                      icon={<BaseIcon icon={faUser} nonInteractive />}
                    />

                    <HStack gap={'16px'} width={'100%'} wrap={'wrap'}>
                      <CopyableText
                        value={(value as any)['CityName'] || '---'}
                        label='City'
                        icon={<BaseIcon icon={faMapMarkerAlt} nonInteractive />}
                      />
                      <CopyableText
                        value={value.HotelName || '---'}
                        label='Hotel'
                        icon={<BaseIcon icon={faBuilding} nonInteractive />}
                      />
                    </HStack>

                    <HStack gap={'16px'} width={'100%'} wrap={'wrap'}>
                      <CopyableText
                        value={formatDate(value.CheckInDate)}
                        label='Check In'
                        icon={<BaseIcon icon={faCalendarAlt} nonInteractive />}
                      />
                      <CopyableText
                        value={formatDate(value.CheckOutDate)}
                        label='Check Out'
                        icon={<BaseIcon icon={faCalendarAlt} nonInteractive />}
                      />
                    </HStack>
                  </VStack>
                  <HStack width={'100%'} marginTop={'8px'}>
                    <Spacer />
                    <HStack>
                      <HStack gap={1}>
                        <Text fontSize='sm' fontWeight='bold' color='gray.700'>
                          Flsq:
                        </Text>
                        <FlsqCopyBadge flsq={value.FlsqNumber} />
                      </HStack>

                      <BaseToolbarButton
                        icon={faExternalLinkAlt}
                        height='24px'
                        width='auto'
                        toolTip='Open Activity'
                        onClick={() => {
                          tabs.tabState.open({
                            tabData: { id: value.ActivityId },
                            key: '',
                            title: `loading`
                          });
                        }}>
                        Open
                      </BaseToolbarButton>

                      <Badge variant={'outline'} borderRadius={'4px'} padding={'4px 8px 4px 8px'} color={'black'}>
                        Status: {value.ActivityStatus}
                      </Badge>
                    </HStack>
                  </HStack>
                </VStack>
              </BaseCard>
            );
          })}
        </Box>
      </BaseDialog>
    </Box>
  );
};
